import StaticDatasetDependenciesPopover from './StaticDatasetDependenciesPopover.tsx';
import useStaticVersionResources from '@/hooks/api/datasets/static/useStaticVersionResources.ts';
import RemoveStaticDatasetModal from './RemoveStaticDatasetModal.tsx';
import { Box, Divider, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import useRetrieveIntegrations from '@/hooks/api/integrations/useRetrieveIntegrations.ts';
import ConnectedIntegration from '@/pages/Editor/components/StepSidebar/DataSources/ConnectedIntegration.tsx';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import UnconnectedIntegration from '@/pages/Editor/components/StepSidebar/DataSources/UnconnectedIntegration.tsx';
import useRetrieveStaticDatasets from '@/hooks/api/datasets/static/useRetrieveStaticDatasets.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import FileItem from '@/pages/AutomationPage/components/ManualRunModal/components/FileItem.tsx';
import UploadNewFiles from '@/pages/Editor/components/StepSidebar/DataSources/UploadNewFiles.tsx';
import InputLabelWithHoverCard from '@/components/InputLabelWithHoverCard.tsx';
import { StaticDataset } from '@/types/datasets.ts';
import ReplaceStaticDatasetModal from '@/pages/Editor/components/StepSidebar/DataSources/ReplaceStaticDatasetModal.tsx';
import StaticReplacementStatus from './StaticReplacementStatus.tsx';

export function DataSourceForm() {
  const [datasetToDelete, setDatasetToDelete] = useState<StaticDataset | undefined>(undefined);
  const [datasetToReplace, setDatasetToReplace] = useState<StaticDataset | undefined>(undefined);

  const {
    data: staticDatasets,
    isPending: loadingStaticDatasets,
    isError: staticDatasetsError,
  } = useRetrieveStaticDatasets();

  const { data: integrations, isPending: loadingIntegrations } = useRetrieveIntegrations();

  const linkedDatasetIntegrations =
    integrations?.results.filter((i) => i.supports_linked_datasets && i.instances.length > 0) || [];

  const unlinkedIntegrationProviders =
    integrations?.results.filter((i) => i.supports_linked_datasets && i.instances.length === 0) ||
    [];

  const { data: versionResources } = useStaticVersionResources();
  const selectedBySource = new Map(
    versionResources?.resources.map((resource) => [resource.source_id, resource]),
  );

  return (
    <Stack>
      <InputLabelWithHoverCard
        label={
          <Text size={'14px'} fw={400}>
            Knowledge Source
          </Text>
        }
        hoverText={
          'A knowledge source is a document or app like Google Sheets that your automation can use in your workflow to pull information from'
        }
      />
      <Box>
        {/*<StyledInputLabel>Manual Upload</StyledInputLabel>*/}
        <Stack gap={'6px'}>
          <Stack mb={'xs'}>
            <UploadNewFiles />
          </Stack>
          {loadingStaticDatasets && <CustomSpinner />}
          {staticDatasetsError && (
            <Text size="xs" c="red" role="alert">
              Data sources could not be loaded. Refresh to try again.
            </Text>
          )}
          {staticDatasets?.results.map((s) => (
            <FileItem
              key={s.id}
              name={selectedBySource.get(s.id)?.name ?? s.available_dataset?.name ?? s.dataset.name}
              onDelete={() => setDatasetToDelete(s)}
              onReplace={() => setDatasetToReplace(s)}
              datasetId={
                selectedBySource.get(s.id)?.dataset_id ?? s.available_dataset?.id ?? s.dataset.id
              }
              py={'6px'}
              removeLabel={`Remove ${selectedBySource.get(s.id)?.name ?? s.available_dataset?.name ?? s.dataset.name}`}
              actions={
                <StaticDatasetDependenciesPopover
                  dataset={s}
                  name={
                    selectedBySource.get(s.id)?.name ?? s.available_dataset?.name ?? s.dataset.name
                  }
                />
              }
              metadata={<StaticReplacementStatus source={s} />}
            />
          ))}
        </Stack>
      </Box>

      <Divider />

      <Stack gap={0}>
        <StyledInputLabel>Integrations</StyledInputLabel>
        <Stack>
          <CustomLoadingOverlay visible={loadingIntegrations} />

          {linkedDatasetIntegrations.map((p) => (
            <ConnectedIntegration provider={p} key={p.name} />
          ))}

          {unlinkedIntegrationProviders.map((p) => (
            <UnconnectedIntegration provider={p} key={p.name} />
          ))}
        </Stack>
      </Stack>

      {datasetToDelete && (
        <RemoveStaticDatasetModal
          key={datasetToDelete.id}
          dataset={datasetToDelete}
          sourceName={
            selectedBySource.get(datasetToDelete.id)?.name ??
            datasetToDelete.available_dataset?.name ??
            datasetToDelete.dataset.name
          }
          onClose={() => setDatasetToDelete(undefined)}
        />
      )}
      <ReplaceStaticDatasetModal
        opened={!!datasetToReplace}
        staticDataset={datasetToReplace}
        sourceName={
          datasetToReplace &&
          (selectedBySource.get(datasetToReplace.id)?.name ??
            datasetToReplace.available_dataset?.name ??
            datasetToReplace.dataset.name)
        }
        onClose={() => setDatasetToReplace(undefined)}
      />
    </Stack>
  );
}
